import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useInView } from 'react-intersection-observer';
import {
  MEDIA_BREAKPOINTS,
  SHORT_HEIGHT,
  ULTRA_NARROW,
} from '@/constants/layout';
import type { Fonts } from '@/theme/themes';

interface AnimatedTitleContainerProps {
  $isAnimated: boolean;
}

const AnimatedTitleContainer = styled.div.attrs({
  dir: 'rtl',
})<AnimatedTitleContainerProps>`
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  padding-inline: 8px;
  overflow: visible;

  opacity: ${({ $isAnimated }) => ($isAnimated ? '1' : '0')};
  transform: ${({ $isAnimated }) =>
    $isAnimated ? 'translateY(0)' : 'translateY(20px)'};
  transition:
    opacity 0.5s ease,
    transform 0.5s ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    transform: none;
    opacity: 1;
  }
`;

interface AnimatedTitleProps {
  fontFamily?: keyof Fonts;
  $mobileScale?: number;
}

const AnimatedTitle = styled.h1<AnimatedTitleProps>`
  && {
    all: unset;
    display: block;
    box-sizing: border-box;

    text-align: center;
    width: 100%;
    max-width: 100%;
    margin: 5px auto;
    padding-inline: 2px;
    color: ${({ theme }) => theme.titleColor};

    font-family: ${({ theme, fontFamily }) =>
      fontFamily ? theme.fonts[fontFamily] : theme.fonts.rubikExtraBold};

    font-weight: 800;
    line-height: 1.1;
    letter-spacing: normal;
    text-transform: none;

    text-shadow:
      -1.4px -1.4px 1.4px ${({ theme }) => theme.shadowLightColor},
      1.4px -1.4px 1.4px ${({ theme }) => theme.shadowLightColor},
      -1.4px 1.4px 1.4px ${({ theme }) => theme.shadowLightColor},
      1.4px 1.4px 1.4px ${({ theme }) => theme.shadowLightColor};

    white-space: nowrap;
    overflow-wrap: break-word;
    word-break: normal;

    /* ---------- Mobile: allow wrapping ---------- */
    @media (max-width: ${MEDIA_BREAKPOINTS.smallScreen}) {
      max-width: calc(100vw - 40px);
      padding-inline: 6px;
      white-space: normal;
      overflow-wrap: anywhere;
      text-wrap: balance;
    }

    @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
      max-width: calc(100vw - 24px);
      white-space: normal;
      overflow-wrap: anywhere;
      text-wrap: balance;
    }

    /* ---------- Mobile Base ---------- */
    font-size: ${({ theme }) => theme.sizes.small.titleFontSize};

    @media (max-width: ${MEDIA_BREAKPOINTS.smallScreen}) {
      font-size: ${({ theme, $mobileScale = 1 }) =>
        `calc(${theme.sizes.small.titleFontSize} * ${$mobileScale})`};
    }

    /* ---------- Tablet ---------- */
    @media (min-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
      font-size: ${({ theme }) => theme.sizes.medium.titleFontSize};
    }

    /* ---------- Desktop ---------- */
    @media (min-width: ${MEDIA_BREAKPOINTS.largeScreen}) {
      font-size: ${({ theme }) => theme.sizes.large.titleFontSize};
      margin: 20px auto; /* 👈 מרווח מעל ומתחת */
    }

    /* כשהגובה נמוך */
    @media (max-height: 900px) {
      margin: 5px auto;
    }

    /* ממש נמוך */
    @media (max-height: 650px) {
      margin: 4px auto;
    }

    /* ---------- Ultra Wide / 2K / 4K ---------- */
    @media (min-width: ${MEDIA_BREAKPOINTS.ultraWide}) {
      font-size: ${({ theme }) => theme.sizes.ultra.titleFontSize};
      margin: 25px auto;
    }

    /* ---------- Very small screens ---------- */
    @media (max-width: ${ULTRA_NARROW}), (max-height: ${SHORT_HEIGHT}) {
      font-size: ${({ theme, $mobileScale = 1 }) =>
        `calc(${theme.sizes.small.titleFontSize} * ${$mobileScale})`};
      line-height: 1.12;
    }
  }
`;

export interface TitleProps {
  children?: React.ReactNode;
  fontFamily?: keyof Fonts;
  template?: string;
  keywords?: { service?: string; city?: string };
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'span';
  itemProp?: string;
  id?: string;
  className?: string;
  mobileScale?: number;
  initiallyVisible?: boolean;
}

const Title: React.FC<TitleProps> = ({
  children,
  fontFamily,
  template,
  keywords,
  as = 'h2',
  itemProp,
  id,
  className,
  mobileScale = 1,
  initiallyVisible = false,
}) => {
  const [isAnimated, setIsAnimated] = useState(initiallyVisible);

  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  useEffect(() => {
    if (initiallyVisible) {
      setIsAnimated(true);
      return;
    }

    setIsAnimated(inView);
  }, [initiallyVisible, inView]);

  const textFromTemplate = useMemo(() => {
    if (!template) return null;

    return template
      .replace('{service}', keywords?.service ?? '')
      .replace('{city}', keywords?.city ?? '');
  }, [template, keywords?.service, keywords?.city]);

  return (
    <AnimatedTitleContainer
      $isAnimated={isAnimated}
      ref={ref}
      className={className}
    >
      <AnimatedTitle
        as={as}
        fontFamily={fontFamily}
        itemProp={itemProp}
        id={id}
        $mobileScale={mobileScale}
      >
        {textFromTemplate ?? children}
      </AnimatedTitle>
    </AnimatedTitleContainer>
  );
};

export default Title;
